import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addDaysToDateString, getEffectiveToday } from "../components/helpers/DateHelper";

export type EventType = {
  id: string;
  date: string;
  title: string;
  timeFrom: string;
  timeTill: string;
  important: boolean;
  reminder: boolean;
  isBirthday: boolean;
  repeat: string;
  description: string;
  deleted: boolean;
};

export type eventsState = {
  userSelectedDate: string;
  effectiveToday: string;
  events: EventType[];
  reminderEvents: EventType[];
  eventToDelete: string;
  eventToEdit: EventType | null;
  eventToConfirmDelete: EventType | null;
};

// Birthdays are just regular events with isBirthday: true and repeat:
// "yearly" now (not a separate static list) - so they flow through the
// same reminder/repeat-advance logic as everything else.
const getReminderEvents = (events: EventType[]): EventType[] => {
  const tomorrowDate = addDaysToDateString(getEffectiveToday(), 1);
  return events.filter((event) => event.reminder && event.date === tomorrowDate);
};

const initialState: eventsState = {
  userSelectedDate: getEffectiveToday(),
  effectiveToday: getEffectiveToday(),
  events: [],
  reminderEvents: [],
  eventToDelete: "",
  eventToEdit: null,
  eventToConfirmDelete: null,
};

const eventsSlice = createSlice({
  name: "events",
  initialState: initialState,
  reducers: {
    setDate(state, action) {
      state.eventToDelete = "";
      state.eventToEdit = null;
      state.userSelectedDate = action.payload.date;
    },
    setDeleteEvent(state, action) {
      state.eventToDelete = action.payload.item;
    },
    unsetDeleteEvent(state) {
      state.eventToDelete = "";
    },
    setEditEvent(state, action) {
      state.eventToEdit = action.payload;
    },
    unsetEditEvent(state) {
      state.eventToEdit = null;
    },
    setConfirmDelete(state, action) {
      state.eventToConfirmDelete = action.payload;
    },
    unsetConfirmDelete(state) {
      state.eventToConfirmDelete = null;
    },
    setEffectiveToday(state, action) {
      // if the user was viewing "today" when the day rolled over, follow it forward
      if (state.userSelectedDate === state.effectiveToday) {
        state.userSelectedDate = action.payload.date;
      }
      state.effectiveToday = action.payload.date;
      state.reminderEvents = getReminderEvents(state.events);
    },
    fetch(state, action) {
      state.events = action.payload.events;
      state.reminderEvents = getReminderEvents(action.payload.events);
    },
    // these mirror what a refetch would produce after a successful add/edit/delete,
    // applied locally so every event mutation doesn't re-download the full list
    addEvent(state, action: PayloadAction<EventType>) {
      state.events.push(action.payload);
      state.reminderEvents = getReminderEvents(state.events);
    },
    updateEvent(state, action: PayloadAction<EventType>) {
      const index = state.events.findIndex((event) => event.id === action.payload.id);
      if (index !== -1) {
        state.events[index] = action.payload;
      }
      state.reminderEvents = getReminderEvents(state.events);
    },
    removeEvent(state, action: PayloadAction<{ id: string }>) {
      state.events = state.events.filter((event) => event.id !== action.payload.id);
      state.reminderEvents = getReminderEvents(state.events);
    },
  },
});

export default eventsSlice.reducer;
export const EventsActions = eventsSlice.actions;
